import { DateTime } from 'luxon';

interface formatDateProps {
  date: Date;
  formatString: string;
}

export const formatDate = ({ date, formatString }: formatDateProps) => {
  return DateTime.fromJSDate(date).toFormat(formatString);
};

export const formatDateString = (
  date1?: string | Date | null,
  format?: string,
): string => {
  if (date1 === undefined || date1 === null) return '';
  const date =
    date1 instanceof Date
      ? DateTime.fromJSDate(date1)
      : DateTime.fromISO(date1);

  return date.toFormat(format ?? 'MM/dd/yyyy');
};

export const timeZoneAbbreviation = () => {
  // Create a DateTime object with the current date
  const now = DateTime.now();
  // Get the time zone abbreviation
  return now.toFormat('z');
};

export const convertUtcToLocalAndFormat = (
  date: string,
  format: string,
): string => {
  return DateTime.fromISO(date, {
    zone: 'utc',
  })
    .setZone('local')
    .toFormat(format);
};

const padTwoDigits = (value: number): string =>
  value.toString().padStart(2, '0');

const MILITARY_TIME_WITH_COLON = /^(\d{1,2}):(\d{0,2})$/;
const MILITARY_TIME_DIGITS = /^(\d{1,2})(\d{2})?$/;

export const parseMilitaryTime = (input: string): string | null => {
  const trimmed = input.trim();
  const candidate = trimmed.includes(':')
    ? trimmed
    : trimmed.replace(/\D/g, '').slice(0, 4);
  const match =
    MILITARY_TIME_WITH_COLON.exec(candidate) ??
    MILITARY_TIME_DIGITS.exec(candidate);
  if (!match) return null;

  const hours = match[1] ? Number(match[1]) : 0;
  const minutes = match[2] ? Number(match[2]) : 0;
  if (hours > 23 || minutes > 59) return null;
  return `${padTwoDigits(hours)}:${padTwoDigits(minutes)}`;
};

export const sanitizeTimeInput = (raw: string): string => {
  const cleaned = raw.replace(/[^\d:]/g, '');
  const colonAt = cleaned.indexOf(':');

  if (colonAt !== -1) {
    const hours = cleaned.slice(0, colonAt);
    const minutes = cleaned.slice(colonAt + 1).replace(/:/g, '');

    if (hours === '' && minutes !== '') return `:${minutes.slice(0, 2)}`;

    const hourOverflowed = hours.length === 1 && minutes.length > 2;
    if (hours !== '' && !hourOverflowed) {
      const nextMinutes = `${hours.slice(2)}${minutes}`;
      return `${hours.slice(0, 2)}:${nextMinutes.slice(0, 2)}`;
    }
  }

  const digits = cleaned.replace(/:/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;

  const hourLength = digits.length === 3 ? 1 : 2;
  return `${digits.slice(0, hourLength)}:${digits.slice(hourLength)}`;
};

export const caretAfterDigits = (
  formatted: string,
  digitCount: number,
  crossSeparator = true,
): number => {
  let index = 0;
  let seen = 0;
  while (seen < digitCount && index < formatted.length) {
    if (/\d/.test(formatted[index] ?? '')) seen += 1;
    index += 1;
  }

  if (crossSeparator) {
    while (formatted[index] === ':') index += 1;
  }
  return index;
};

export const timeStringToDate = (timeString: string): Date => {
  const [hours = 0, minutes = 0] = timeString.split(':').map(Number);
  const date = new Date();
  date.setHours(hours, minutes, 0, 0);
  return date;
};

export const dateToTimeString = (date: Date): string =>
  `${padTwoDigits(date.getHours())}:${padTwoDigits(date.getMinutes())}`;

export const timeIntegerToTimeString = (
  timeInt: number | null | undefined,
): string => {
  if (timeInt === null || timeInt === undefined) return '';
  const str = timeInt.toString().padStart(4, '0');
  return `${str.slice(0, 2)}:${str.slice(2, 4)}`;
};

export const timeStringToTimeInteger = (
  timeString: string | null | undefined,
): number | null => {
  const parts = (timeString ?? '').split(':');
  const parsed = parseMilitaryTime(
    parts.length > 1 ? `${parts[0]}:${parts[1]}` : parts[0],
  );
  if (parsed === null) return null;
  const value = Number(parsed.replace(':', ''));
  return Number.isNaN(value) ? null : value;
};

/** Returns an invalid Date for unparseable input so flatpickr rejects it instead of falling back to midnight. */
export const militaryTimeToDate = (input: string): Date => {
  const parsed = parseMilitaryTime(input);
  return parsed ? timeStringToDate(parsed) : new Date(NaN);
};

export const formatDateOrNull = (date: string | null): string | null => {
  if (date === null) return null;
  if (typeof date !== 'string' || date.trim() === '') return null;
  const dateFormatRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/\d{4}$/;
  if (dateFormatRegex.test(date.trim())) {
    return date;
  }
  return formatDateString(date);
};
